import { API_BASE } from '../apiBase';
// Talking to the assistant endpoint.
//
// Self-contained on purpose. The equivalents in App.js sit 17,000 lines into a
// 53,000-line file, and importing from there would mean this panel could not be
// moved, tested, or code-split without dragging the whole application with it.
// The duplication is a few lines; the coupling would be permanent.

const ENGINE = API_BASE + "/api/survai-engine";

// A stale access_token is accepted by getSession() and rejected by the engine
// with a 401 that looks like a permissions problem. Refresh anything within a
// minute of expiry rather than let someone read "unauthorized" and conclude
// they have lost access.
async function authHeaders() {
  const base = { "Content-Type": "application/json" };
  try {
    const { supabase } = await import("../supabaseClient");
    if (!supabase) return base;
    let { data } = await supabase.auth.getSession();
    let session = data?.session;
    const expiringSoon = session?.expires_at ? session.expires_at * 1000 < Date.now() + 60000 : false;
    if (!session?.access_token || expiringSoon) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session?.access_token) session = refreshed.session;
    }
    return session?.access_token ? { ...base, Authorization: `Bearer ${session.access_token}` } : base;
  } catch (_) {
    return base;
  }
}

/**
 * @param {string} question
 * @param {Array<{role:string, content:string}>} history
 * @param {string|null} facilityCcn
 * @param {Array<{kind:string,id:string,label:string}>} subjects
 *   What the PREVIOUS answer was about, handed back so a follow-up does not
 *   have to name it again. Asked "who are my repeat fallers" and then "name",
 *   the server had no subject to attach the second question to and replied
 *   asking what was meant. These are identifiers the server itself returned,
 *   lifted from rows it retrieved — it re-checks and re-scopes every one.
 * @param {Array<{retriever:string,params:object}>} lastPlan
 *   The query behind the previous answer, so "now make it 30 days" moves one
 *   filter instead of re-deriving the whole plan and often landing on a
 *   different retriever. The server re-validates it and drops any parameter a
 *   retriever does not declare.
 * @returns {Promise<{answer:string, retrieval:object, subjects:Array, lastPlan:Array}>}
 */
export async function askAssistant(question, history = [], facilityCcn = null, subjects = [], lastPlan = []) {
  const res = await fetch(`${ENGINE}/chat`, {
    method: "POST",
    headers: await authHeaders(),
    // Only the last few turns. The server caps this too — the cap here is so a
    // long conversation does not slow down before the server trims it.
    body: JSON.stringify({ question, history: history.slice(-8), facilityCcn, subjects, lastPlan }),
  });

  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON error page */ }

  if (!res.ok) {
    // The server distinguishes "could not reach the model" from "nothing was
    // found", and that distinction has to survive to the screen or the whole
    // point of it is lost one layer from the end.
    const message = body?.error?.message || body?.message || `The assistant could not be reached (${res.status}).`;
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  const data = body?.data ?? body ?? {};
  return {
    answer: String(data.answer ?? ""),
    retrieval: data.retrieval ?? null,
    subjects: Array.isArray(data.subjects) ? data.subjects : [],
    lastPlan: Array.isArray(data.lastPlan) ? data.lastPlan : [],
  };
}

/**
 * What this session may browse, and whether the Ask tab can work at all.
 * Drawn from the server so the UI never offers a query the server would refuse.
 */
/**
 * Make a change Atlas proposed.
 *
 * Called ONLY from a button somebody pressed. There is no path from a model
 * reply to this function and there must not be one: the model proposes, a
 * person decides. See survai-engine/src/chat/actions.mjs.
 */
export async function actOnAtlas(action, params, facilityCcn = null) {
  const res = await fetch(`${ENGINE}/chat/act`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ action, params, facilityCcn }),
  });

  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON error page */ }

  if (!res.ok) {
    // The server's own words. It distinguishes "needs a reason" from "that
    // finding is not within your scope", and flattening both to "failed" loses
    // the one the person can actually act on.
    const err = new Error(body?.error?.message || `The change did not go through (${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return body?.data ?? body ?? {};
}

export async function assistantContext(facilityCcn = null) {
  const qs = facilityCcn ? `?facilityCcn=${encodeURIComponent(facilityCcn)}` : "";
  const res = await fetch(`${ENGINE}/chat/context${qs}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Could not read your access (${res.status}).`);
  const body = await res.json();
  return body?.data ?? body ?? {};
}

/**
 * Run one stored query directly — no model, no cost, same scope and the same
 * computed completeness the assistant sees.
 */
export async function runQuery(retriever, params = {}, facilityCcn = null) {
  const res = await fetch(`${ENGINE}/chat/retrieve`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ retriever, params, facilityCcn }),
  });
  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON error page */ }
  if (!res.ok) {
    const err = new Error(body?.error?.message || body?.message || `That query could not be run (${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return body?.data ?? body ?? {};
}

export { ENGINE };

/**
 * Build the semantic note index, one bounded pass at a time.
 *
 * docs/embeddings.sql creates the table; this is what fills it. Incremental by
 * note hash, so calling it repeatedly works through a backlog and a note
 * already embedded costs nothing.
 *
 * Platform support only — it spends money per note against Bedrock and writes
 * vectors that are themselves PHI. The server enforces that; the UI only hides
 * a button that would 403.
 */
export async function embedNotes({ batch = 50, maxNotes = 500 } = {}) {
  const res = await fetch(`${ENGINE}/chat/embed-notes`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ batch, maxNotes }),
  });
  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON error page */ }
  if (!res.ok) {
    const err = new Error(body?.error?.message || body?.message || `The index could not be built (${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return body?.data ?? body ?? {};
}
